/**
 * Delivery state for anything queued for later sending.
 *
 * Shared by two queues that fail differently: the operator app's submission outbox,
 * where a record is never abandoned however long a phone stays out of signal, and the
 * server's notification outbox, where a webhook that has failed for a day should stop
 * retrying and wake a person instead. The difference lives entirely in the schedule.
 *
 * Pure: no clock, no storage. `nowMs` and `randomFraction` come in as parameters,
 * which is what lets "never abandoned" be asserted five hundred failures deep in a
 * test that runs in a millisecond.
 */

import { isExhausted, nextAttemptDelayMs, type BackoffSchedule } from './retry.ts';

export type DeliveryStatus =
  /** Waiting to be sent, and will be retried. */
  | 'queued'
  /** Claimed by a sender right now. */
  | 'sending'
  /** Accepted by the server. Terminal. */
  | 'sent'
  /** Rejected in a way retrying cannot fix. Terminal, and needs a person. */
  | 'failed'
  /** Ran out of attempts. Terminal, and escalates. Only reachable with a maxAttempts schedule. */
  | 'dead';

export interface DeliveryState {
  readonly status: DeliveryStatus;
  readonly attempts: number;
  readonly nextAttemptAtMs: number;
  readonly lastError?: string;
}

export interface FailureContext {
  readonly state: DeliveryState;
  readonly schedule: BackoffSchedule;
  readonly reason: string;
  /** False for a rejection the server will simply repeat. */
  readonly retryable: boolean;
  readonly nowMs: number;
  readonly randomFraction: number;
}

export function newDelivery(nowMs: number): DeliveryState {
  return { status: 'queued', attempts: 0, nextAttemptAtMs: nowMs };
}

export function afterSuccess(state: DeliveryState): DeliveryState {
  return { ...state, status: 'sent' };
}

export function afterFailure(context: FailureContext): DeliveryState {
  const attempts = context.state.attempts + 1;
  const base = { attempts, lastError: context.reason };

  // A rejection the server will repeat is not a network problem. Retrying it burns
  // battery and hides a real fault behind an ever-growing backoff, so it stops here
  // and surfaces to a person instead.
  if (!context.retryable) {
    return { ...base, status: 'failed', nextAttemptAtMs: context.state.nextAttemptAtMs };
  }

  if (isExhausted(context.schedule, attempts)) {
    return { ...base, status: 'dead', nextAttemptAtMs: context.state.nextAttemptAtMs };
  }

  return {
    ...base,
    status: 'queued',
    nextAttemptAtMs: context.nowMs + nextAttemptDelayMs(context.schedule, attempts, context.randomFraction),
  };
}

/** True when a sender should pick this row up now. */
export function isDue(state: DeliveryState, nowMs: number): boolean {
  return state.status === 'queued' && state.nextAttemptAtMs <= nowMs;
}

const RETRYABLE_CLIENT_STATUSES = new Set([
  408, // request timeout
  425, // too early
  429, // rate limited
]);

const SERVER_FAULT_FLOOR = 500;

/**
 * `undefined` means the request never reached a server at all, which in a metal
 * building is the ordinary case rather than an exceptional one.
 */
export function isRetryableStatus(httpStatus: number | undefined): boolean {
  if (httpStatus === undefined) {
    return true;
  }

  return httpStatus >= SERVER_FAULT_FLOOR || RETRYABLE_CLIENT_STATUSES.has(httpStatus);
}
